import React, { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { MapPin, Play, Square, Car, Loader2, FileSpreadsheet, Download } from "lucide-react";
import { uniqueRanches } from "../lib/ranchUtils";

const M_PER_MILE = 1609.344;
const haversine = (a, b) => {
  const R = 6371000, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

export default function Mileage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "supervisor"].includes(user?.role);
  const [ranches, setRanches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobcodes, setJobcodes] = useState([]);
  const [ranchId, setRanchId] = useState("");
  const [jobCodeId, setJobCodeId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [tracking, setTracking] = useState(false);
  const [meters, setMeters] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [gpsErr, setGpsErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState({ rows: [], total_miles: 0 });

  const watchId = useRef(null);
  const lastPos = useRef(null);
  const startPos = useRef(null);
  const startTime = useRef(null);
  const timer = useRef(null);

  const load = async () => {
    const [r, l, j] = await Promise.all([api.get("/ranches"), api.get("/mileage"), api.get("/jobcodes")]);
    setRanches(r.data); setLogs(l.data); setJobcodes(j.data.filter((x) => x.active !== false));
    if (isAdmin) { const e = await api.get("/employees"); setEmployees(e.data); }
  };
  useEffect(() => { load(); return () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); if (timer.current) clearInterval(timer.current); }; }, []);

  const onPos = (pos) => {
    const { latitude, longitude, accuracy } = pos.coords;
    if (accuracy && accuracy > 100) return; // ignore low-accuracy fixes
    const p = { lat: latitude, lng: longitude };
    if (!startPos.current) startPos.current = p;
    if (lastPos.current) {
      const d = haversine(lastPos.current, p);
      if (d > 8 && d < 2000) setMeters((m) => m + d); // filter jitter + GPS jumps
    }
    lastPos.current = p;
  };

  const start = () => {
    if (!ranchId) { setGpsErr(t("mil.err_ranch")); return; }
    if (!jobCodeId) { setGpsErr(t("mil.err_jobcode")); return; }
    if (!navigator.geolocation) { setGpsErr(t("mil.err_gps")); return; }
    setGpsErr(""); setMeters(0); setElapsed(0);
    lastPos.current = null; startPos.current = null; startTime.current = new Date().toISOString();
    watchId.current = navigator.geolocation.watchPosition(onPos, (e) => setGpsErr(e.message), { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
    timer.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    setTracking(true);
  };

  const end = async () => {
    if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    setTracking(false);
    const miles = +(meters / M_PER_MILE).toFixed(2);
    setSaving(true);
    try {
      await api.post("/mileage", {
        ranch_id: ranchId, job_code_id: jobCodeId, miles,
        start_time: startTime.current, end_time: new Date().toISOString(),
        start_lat: startPos.current?.lat, start_lng: startPos.current?.lng,
        end_lat: lastPos.current?.lat, end_lng: lastPos.current?.lng,
        employee_id: isAdmin ? employeeId : undefined,
      });
      setMeters(0); setElapsed(0);
      await load();
    } catch (e) {
      setGpsErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const download = async (fmt) => {
    const token = getToken();
    const res = await fetch(`${API}/mileage/export?fmt=${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `mileage.${fmt}`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const miles = (meters / M_PER_MILE).toFixed(2);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const L = "text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1";

  return (
    <div className="space-y-6 max-w-xl mx-auto" data-testid="mileage-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("nav.mileage")}</h1>
        <p className="text-sm text-stone-500 mt-1 flex items-center gap-1"><Car size={15} /> {t("mil.sub")}</p>
      </div>

      <div className="bg-white border border-stone-200 p-5 space-y-4">
        {isAdmin && (
          <div><label className={L}>{t("mil.employee")}</label>
            <select data-testid="mileage-employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} disabled={tracking} className="w-full h-12 px-3 border border-stone-200 bg-white">
              <option value="">{t("mil.myself")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        )}
        <div><label className={L}>{t("mil.ranch")}</label>
          <select data-testid="mileage-ranch" value={ranchId} onChange={(e) => setRanchId(e.target.value)} disabled={tracking} className="w-full h-12 px-3 border border-stone-200 bg-white">
            <option value="">{t("mil.select_ranch")}</option>
            {uniqueRanches(ranches).map((r) => <option key={r.id} value={r.id}>{r.ranch_number} · {r.name}{r.location ? ` — ${r.location}` : ""}</option>)}
          </select>
        </div>
        <div><label className={L}>{t("mil.jobcode")}</label>
          <select data-testid="mileage-jobcode" value={jobCodeId} onChange={(e) => setJobCodeId(e.target.value)} disabled={tracking} className="w-full h-12 px-3 border border-stone-200 bg-white">
            <option value="">{t("mil.select_jobcode")}</option>
            {jobcodes.map((j) => <option key={j.id} value={j.id}>{j.job_code || j.code} · {j.job || j.description}</option>)}
          </select>
        </div>

        <div className="text-center py-4">
          <div data-testid="mileage-miles" className="text-6xl font-bold tabular-nums" style={{ fontFamily: "'Fraunces', serif" }}>{miles}</div>
          <div className="text-xs uppercase tracking-wide text-stone-500">{t("mil.miles")} · {mm}:{ss} {tracking && <span className="text-[#1F3D34] font-bold">● {t("mil.tracking")}</span>}</div>
        </div>

        {gpsErr && <div data-testid="mileage-error" className="text-sm text-red-700 bg-red-50 border border-red-200 p-2">{gpsErr}</div>}

        {!tracking ? (
          <button data-testid="mileage-start" onClick={start} disabled={saving} className="w-full h-16 bg-[#1F3D34] text-white font-bold uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="animate-spin" /> : <Play weight="fill" />} {t("mil.start")}
          </button>
        ) : (
          <button data-testid="mileage-end" onClick={end} className="w-full h-16 bg-red-700 text-white font-bold uppercase tracking-wide flex items-center justify-center gap-2">
            <Square /> {t("mil.end")}
          </button>
        )}
      </div>

      <div className="bg-white border border-stone-200 overflow-x-auto">
        <div className="flex justify-between items-center p-3 border-b border-stone-200">
          <span className="text-sm font-bold uppercase tracking-wide">{t("mil.trips")}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500">{logs.total_miles} {t("mil.total")}</span>
            {canManage && (
              <>
                <button data-testid="mileage-export-xlsx" onClick={() => download("xlsx")} className="h-9 px-3 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-1"><FileSpreadsheet size={14} /> Excel</button>
                <button data-testid="mileage-export-csv" onClick={() => download("csv")} className="h-9 px-3 border border-stone-300 font-bold uppercase text-xs flex items-center gap-1"><Download size={14} /> CSV</button>
              </>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs"><tr className="text-left">
            <th className="p-3">{t("mil.date")}</th><th className="p-3">{t("mil.driver")}</th><th className="p-3">{t("mil.ranch_col")}</th>
            <th className="p-3">{t("mil.job")}</th>
            <th className="p-3 text-right">{t("mil.miles_col")}</th>
          </tr></thead>
          <tbody>
            {logs.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-stone-500 font-bold uppercase">{t("mil.no_trips")}</td></tr>}
            {logs.rows.map((m) => (
              <tr key={m.id} className="border-b border-stone-100" data-testid={`mileage-row-${m.id}`}>
                <td className="p-3 font-mono text-xs">{m.trip_date}</td>
                <td className="p-3">{m.employee_name}</td>
                <td className="p-3 text-xs">{m.ranch_name}</td>
                <td className="p-3 text-xs">{m.job_name || m.job_code || "—"}</td>
                <td className="p-3 text-right font-mono font-bold">{m.miles}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
